import Scene = Phaser.Scene;
import {Keys} from "@/constants/key";
import Image = Phaser.GameObjects.Image;
import TextUtils from "@/utils/text-utils";
import Text = Phaser.GameObjects.Text;
import TimerEvent = Phaser.Time.TimerEvent;
import {Fonts} from "@/constants/font";
import {KeysNoLoad} from "@/constants/keys-no-load";
import SpriteUtils from "@/utils/sprite-utils";

export default class EducationPanel extends Phaser.GameObjects.Container {

    private blackoutDelay: number = 800;
    private static readonly BLACKOUT_DURATION = 500;
    private static readonly TEACHER_DURATION = 500;
    public static TEXT_DELAY = 100;

    private textPrintedCallback: (() => unknown) | undefined;
    private titleText: Text | undefined;
    private teacherImage: Image | undefined;
    private messageText: Text | undefined;
    private teacherKey: string | undefined;
    private title: string | undefined;

    public static DEPTH: number = 1001;

    private currMessage: string = '';
    private index = 0;
    private letters: string[] = [];
    private fullText: string | undefined;

    private blackoutImage: Image | undefined;
    private textTimerEvent: TimerEvent | undefined;

    private animating!: boolean;

    private xButton: Image | undefined;

    private cancelCallback: (() => unknown) | undefined;
    private completeEducationCallback!: (() => unknown) | undefined;

    constructor(scene: Scene, blackoutDelay: number, cancelCallback: (() => unknown) | undefined) {
        super(scene);
        this.animating = false;
        this.cancelCallback = cancelCallback;

        this.scene = scene;
        this.x = 0;
        this.y = 0;
        this.setDepth(EducationPanel.DEPTH);
        this.blackoutDelay = blackoutDelay;

        this.blackoutImage = SpriteUtils.addImage(this.scene, 0, 0, Keys.EDU_BLACKOUT)
            .setOrigin(0, 0);

        this.add(this.blackoutImage);
        this.blackoutImage.alpha = 0;

        this.scene.tweens.chain({
            targets: this.blackoutImage,
            tweens: [{
            },
                {
                    delay: this.blackoutDelay,
                    duration: EducationPanel.BLACKOUT_DURATION,
                    alpha: 1
                }]
        });

        let self = this;
        scene.input.on('pointerup', function(pointer: any){
            if (self.active) {
                if (self.fullText && self.messageText && self.completeEducationCallback && self.messageText.text == self.fullText) {
                    self.completeEducationCallback()
                } else {
                    self.completeText();
                }
            }
        });

        if (cancelCallback) {
            this.xButton = SpriteUtils.addImage(this.scene, 570, 25, Keys.UI_SHOP_X).setOrigin(0.5, 0.5);
            this.add(this.xButton);
            this.xButton.setInteractive().on('pointerup', function (pointer: any) {
                cancelCallback();
            });
        }
    }

    public disableClickPropagation() {
        this.blackoutImage!.setInteractive();
    }

    public init(teacherKey: string, text: string, textPrintedCallback: () => unknown, completeEducationCallback?: (() => unknown) | undefined, title: string | undefined = undefined): void {
        this.textPrintedCallback = textPrintedCallback;
        this.fullText = text;
        this.animating = true;
        this.completeEducationCallback = completeEducationCallback;

        if (this.messageText) {
            this.messageText.destroy();
            this.messageText = undefined;
        }

        let textDelay = EducationPanel.TEXT_DELAY;
        if (!this.teacherImage || this.teacherKey !== teacherKey) {
            if (this.teacherImage) {
                this.teacherImage.destroy();
            }

            if (teacherKey === 'black' || teacherKey === 'edu_hathor' || teacherKey === 'edu_sikji') {
                this.teacherImage = SpriteUtils.addImage(this.scene, 35, 20, teacherKey).setOrigin(0, 0)
                    .setOrigin(0, 0);
            } else {
                this.teacherImage = SpriteUtils.addImage(this.scene, 35, 20, EducationPanel.atlas(teacherKey), teacherKey).setOrigin(0, 0)
                    .setOrigin(0, 0);
            }

            this.add(this.teacherImage);
            this.teacherImage.alpha = 0;
            this.scene.tweens.chain({
                targets: this.teacherImage,
                tweens: [{},
                    {
                        delay: this.blackoutDelay + EducationPanel.BLACKOUT_DURATION,
                        duration: EducationPanel.TEACHER_DURATION,
                        alpha: 1
                    }]
            });
            textDelay += (this.blackoutDelay + EducationPanel.BLACKOUT_DURATION + EducationPanel.TEACHER_DURATION);
        }

        if (title) {
            if (!this.titleText || this.title !== title) {
                if (this.titleText) {
                    this.titleText.destroy();
                }
                // this.titleText = this.scene.add.text(188, 5, title).setOrigin(0, 0);

                this.titleText = TextUtils.addTextAdvanced(this.scene, title, 188, 20, '#fff1a0', 16,
                    'normal', 380, 'left', 1, Fonts.MYRIADPRO_BOLD)
                    .setOrigin(0, 0);

                this.add(this.titleText);
                this.titleText.alpha = 0;
                this.scene.tweens.chain({
                    targets: this.titleText,
                    tweens: [{},
                        {
                            delay: this.blackoutDelay + EducationPanel.BLACKOUT_DURATION,
                            duration: EducationPanel.TEACHER_DURATION,
                            alpha: 1
                        }]
                });
            }
        }

        this.textTimerEvent = this.scene.time.delayedCall(textDelay, () => {
            if (this.active) {
                if (this.messageText) {
                    this.messageText.destroy();
                    this.messageText = undefined;
                }

                this.setupMessageText('');

                this.currMessage = '';
                this.index = 0;
                this.letters = text.split('');

                EducationPanel.nextWord(this, this.messageText);
            }
        });

        this.teacherKey = teacherKey;
        this.title = title;
    }

    private static atlas(teacherKey: string) {
        if (KeysNoLoad.EDU_ATLAS_8.includes(teacherKey)) {
            return Keys.ATLAS_EDU_8;
        } else if (KeysNoLoad.EDU_ATLAS_7.includes(teacherKey)) {
            return Keys.ATLAS_EDU_7;
        } else if (KeysNoLoad.EDU_ATLAS_6.includes(teacherKey)) {
            return Keys.ATLAS_EDU_6;
        } else if (KeysNoLoad.EDU_ATLAS_3.includes(teacherKey)) {
            return Keys.ATLAS_EDU_3;
        } else if (KeysNoLoad.EDU_ATLAS_2.includes(teacherKey)) {
            return Keys.ATLAS_EDU_2;
        } else if (KeysNoLoad.EDU_ATLAS_SIDE_QUESTS.includes(teacherKey)){
            return Keys.ATLAS_EDU_SDQ;
        } else if (KeysNoLoad.P9_DIALOG_ATLAS.includes(teacherKey)) {
            return Keys.ATLAS_P9_DIALOG_V2;
        }
        return Keys.ATLAS_EDU;
    }

    public removeText(): void {
        if (this.messageText) {
            this.messageText.destroy();
        }
    }

    public completeText() {
        if (this.textTimerEvent && this.fullText) {
            this.textTimerEvent.destroy();
            this.textTimerEvent = undefined;

            if (this.messageText) {
                this.messageText.destroy();
            }

            this.setupMessageText(this.fullText);

            EducationPanel.callPrintedCallback(this);
        }
    }

    public isAnimating(): boolean {
        return this.animating;
    }

    private static nextWord(self: EducationPanel, messageText: Text | undefined) {
        if (self.active && messageText && messageText!.active) {
            if (self.index < self.letters.length) {
                self.currMessage = self.currMessage.concat(self.letters[self.index]);
                messageText!.setText(self.currMessage)
                let delay = ['.', '!'].includes(self.letters[self.index]) ? 600 : 40;
                delay = ',' == self.letters[self.index] ? 150 : delay;
                delay = ' ' == self.letters[self.index] ? 60 : delay;
                ++self.index;
                self.scene.time.delayedCall(delay, () => EducationPanel.nextWord(self, messageText));
            } else {
                EducationPanel.callPrintedCallback(self);
            }
        }
    }

    private setupMessageText(text: string): void {
        let l = this.fullText ? this.fullText.length : 0;
        let size = l > 150 ? (l > 200 ? (l > 220 ? 11 : 12) : 13) : 15;
        this.messageText = TextUtils.addTextAdvanced(this.scene, text, 188, this.title ? 50 : 33, '#ffffff', size,
            'normal', 380, 'left', 1, Fonts.MYRIADPRO_REGULAR)
            .setOrigin(0, 0);
        this.messageText.setStroke('#000000', 2);
        this.add(this.messageText);
    }

    private static callPrintedCallback(self: EducationPanel): void {
        if (self.textPrintedCallback) {
            self!.textPrintedCallback!();
            self.textPrintedCallback = undefined;
        }
        self.animating = false;
    }
}
